import type { CommandAction } from './command-actions';
import type { CommandDestination } from './command-destinations';
import { foldForSearch } from './command-destinations';

/**
 * One row of the palette, whatever it came from.
 *
 * The palette used to render `CommandDestination` directly, which was honest while it only
 * navigated. Now that it also runs actions and finds entities, the three sources are normalised to
 * ONE row type here, so the keyboard model, the filter and the rendering each exist once rather
 * than three times over — the same reason the rail, the bottom bar and the breadcrumb all read one
 * resolved navigation model.
 *
 * `group` is the section heading a row sits under; `hint` is the trailing context shown ON the row
 * (a section's own module, a study's identifier). Both take part in the match, so typing a module
 * name reaches its screens and typing `azioni` reaches the actions.
 */
export type CommandItem = {
  readonly id: string;
  readonly label: string;
  readonly group: string;
  readonly hint?: string;
  /** `run` rows are marked in the UI, because pressing Enter on one changes something. */
  readonly kind: 'navigate' | 'run';
  /** `true` when the row names the state the app is already in — rendered as a check. */
  readonly current?: boolean;
  readonly perform: () => void;
};

export function navigationItems(
  destinations: readonly CommandDestination[],
  group: string,
  navigate: (path: string) => void,
): readonly CommandItem[] {
  return destinations.map((destination) => ({
    id: destination.id,
    label: destination.label,
    group,
    ...(destination.group === undefined ? {} : { hint: destination.group }),
    kind: 'navigate' as const,
    perform: () => navigate(destination.path),
  }));
}

export function actionItems(
  actions: readonly CommandAction[],
  group: string,
): readonly CommandItem[] {
  return actions.map((action) => ({
    id: action.id,
    label: action.label,
    group,
    kind: 'run' as const,
    current: action.current,
    perform: action.run,
  }));
}

export type CommandEntity = {
  readonly id: string;
  readonly label: string;
  readonly hint?: string;
  readonly path: string;
};

export function entityItems(
  entities: readonly CommandEntity[],
  group: string,
  navigate: (path: string) => void,
): readonly CommandItem[] {
  return entities.map((entity) => ({
    id: entity.id,
    label: entity.label,
    group,
    ...(entity.hint === undefined ? {} : { hint: entity.hint }),
    kind: 'navigate' as const,
    perform: () => navigate(entity.path),
  }));
}

/**
 * Diacritic- and case-insensitive substring match over the label, the hint and the group heading —
 * the same `foldForSearch` both sides, so `attivita` finds `Attività`.
 *
 * ENTITIES ARE EXEMPT FROM THE EMPTY-QUERY LISTING, and that exemption lives at the call site
 * rather than here: an unfiltered palette should open on the places you can go and the things you
 * can do, not on a dump of every study in the organization.
 */
export function filterCommandItems(
  items: readonly CommandItem[],
  query: string,
): readonly CommandItem[] {
  const needle = foldForSearch(query.trim());
  if (needle.length === 0) {
    return items;
  }
  return items.filter((item) =>
    foldForSearch([item.label, item.hint ?? '', item.group].join(' ')).includes(needle),
  );
}
